// ============================================================
// shared/src/dto/admin.dto.ts
// 管理后台分页/筛选 schema
// ============================================================

import { z } from 'zod';

/** 登录请求 */
export const loginRequestSchema = z.object({
  username: z.string().min(1),
  password: z.string().min(1),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

/** 登录响应 */
export interface LoginResponse {
  token: string;
  user: {
    id: number;
    username: string;
    displayName: string;
    role: string;
    scopeChannelId: number | null;
  };
}

/** 分页参数 */
export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

export type PaginationParams = z.infer<typeof paginationSchema>;

/** 订单查询筛选 */
export const orderFilterSchema = paginationSchema.extend({
  appKey: z.string().optional(),
  status: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  // ★ 2026-08-28 新增：创建时间起止（UTC+8 日界，'YYYY-MM-DD' 或 'YYYY-MM-DD HH:mm:ss'）
  startTime: z.string().max(32).optional(),
  endTime: z.string().max(32).optional(),
  // ★ 2026-08-28 新增：下游渠道筛选（PARTNER/DOWNSTREAM 仍受 scope 约束）
  downstreamChannelId: z.coerce.number().int().positive().optional(),
  // ★ 2026-08-28 新增：多状态筛选（逗号分隔，如 INSURED,RENEWED；保单=INSURED/RENEWED/SURRENDERED/TERMINATED）
  statusIn: z.string().max(128).optional(),
  keyword: z.string().optional(),
});

export type OrderFilterParams = z.infer<typeof orderFilterSchema>;

/** 渠道创建/更新 */
export const upstreamChannelSchema = z.object({
  appKey: z.string().min(1).max(64),
  name: z.string().min(1).max(128),
  aesKey: z.string().min(1).max(128),
  jdBaseUrl: z.string().max(256).optional().default(''),
  status: z.enum(['active', 'disabled', 'expired']).optional().default('active'),
});

export type UpstreamChannelInput = z.infer<typeof upstreamChannelSchema>;

export const downstreamChannelSchema = z.object({
  upstreamChannelId: z.number().int().positive(),
  appKey: z.string().min(1).max(64),
  name: z.string().min(1).max(128),
  aesKey: z.string().min(1).max(128),
  callbackUrl: z.string().min(1).max(512),
  pushEnabled: z.boolean().optional().default(true),
  maxRetry: z.number().int().min(1).max(20).optional().default(8),
  fieldMapping: z.record(z.string(), z.string()).nullable().optional().default(null),
  eventTypes: z.array(z.string()).nullable().optional().default(null),
  amountUnit: z.enum(['cent', 'yuan']).optional().default('cent'),
  timeZone: z.string().max(32).optional().default('Asia/Shanghai'),
  status: z.enum(['active', 'disabled', 'expired']).optional().default('active'),
});

export type DownstreamChannelInput = z.infer<typeof downstreamChannelSchema>;

/** 产品链接输入 */
export const productLinkSchema = z.object({
  upstreamChannelId: z.number().int().positive(),
  productId: z.string().min(1).max(64),
  productName: z.string().max(256).optional().default(''),
  rawUrl: z.string().min(1).max(1024),
  status: z.enum(['active', 'disabled']).optional().default('active'),
});

export type ProductLinkInput = z.infer<typeof productLinkSchema>;

/** 用户创建输入 */
export const createUserSchema = z.object({
  username: z.string().min(1).max(64),
  password: z.string().min(6).max(128),
  displayName: z.string().max(64).optional().default(''),
  role: z.enum(['ADMIN', 'OPS', 'TECH', 'PARTNER', 'DOWNSTREAM']).default('DOWNSTREAM'),
  scopeChannelId: z.number().int().positive().nullable().optional().default(null),
});

export type CreateUserInput = z.infer<typeof createUserSchema>;

/** 密码修改 */
export const changePasswordSchema = z.object({
  oldPassword: z.string().min(1),
  newPassword: z.string().min(6).max(128),
});

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/** 加密工具请求 */
export const cryptoEncryptRequestSchema = z.object({
  plaintext: z.string().min(1),
  aesKey: z.string().min(1),
  appKey: z.string().min(1),
});

export type CryptoEncryptRequest = z.infer<typeof cryptoEncryptRequestSchema>;

export const cryptoDecryptRequestSchema = z.object({
  data: z.string().min(1),
  sign: z.string().min(1),
  appKey: z.string().min(1),
  aesKey: z.string().min(1),
});

export type CryptoDecryptRequest = z.infer<typeof cryptoDecryptRequestSchema>;
